import React, { useCallback, useMemo, useState } from 'react';

import { DebouncedAutocomplete, ScrollLoader } from 'components';

import detectElementBottom from 'utils/scroll/detectElementBottom';

import { DefaultPagination } from 'constants/pagination';

import { IInfiniteDebounecedAutocompleteProps } from './InfiniteDebouncedAutocomplete.types';

const InfiniteDebouncedAutocomplete = ({
  value,
  options,
  total = 0,
  pageSize = DefaultPagination.PAGE_LIMIT,
  onSearch,
  ...props
}: IInfiniteDebounecedAutocompleteProps) => {
  const [page, setPage] = useState(DefaultPagination.START_PAGE);
  const [loading, setLoading] = useState(false);
  const [scrollLoading, setScrollLoading] = useState(false);

  const totalPages = useMemo(() => Math.ceil(total / pageSize), [pageSize, total]);

  const handleSearch = useCallback(
    async (search: string) => {
      setLoading(true);

      await onSearch({ search, page: DefaultPagination.START_PAGE, pageSize });

      setPage(DefaultPagination.START_PAGE);
      setLoading(false);
    },
    [onSearch, pageSize],
  );

  const handleSearchMore = async () => {
    const nextPage = page + 1;

    if (nextPage > totalPages || scrollLoading) {
      return;
    }

    setScrollLoading(true);

    await onSearch({ search: value, page: nextPage, pageSize });

    setPage(nextPage);
    setScrollLoading(false);
  };

  const onPopupScroll = event => {
    detectElementBottom(event, handleSearchMore, { deviation: 20 });
  };

  return (
    <DebouncedAutocomplete
      value={value}
      loading={loading}
      options={loading ? [] : options}
      onPopupScroll={onPopupScroll}
      onSearch={handleSearch}
      popupRender={menu => (
        <>
          {menu}
          {scrollLoading && <ScrollLoader />}
        </>
      )}
      {...props}
    />
  );
};

export default InfiniteDebouncedAutocomplete;
